// S49 — Trust hub: Clinical Toolkit methodology + citations.
//
// Public landing page documenting every calculator's source, validity
// range, limitations, and the FDA SaMD positioning. Linked from the PWA
// disclaimer banner.

import { LegalPage } from "../../components/LegalPage";

export function ClinicalToolkitMethodology() {
  return (
    <LegalPage
      title="Clinical toolkit methodology"
      eyebrow="Trust center"
      description="WeConsult Rx clinical toolkit — citations, validity ranges, limitations. Educational reference; not a Clinical Decision Support System."
      path="/trust/clinical-toolkit-methodology"
      effective="2026-05-31"
    >
      <h2>Positioning</h2>
      <p>
        The WeConsult Rx clinical toolkit is an <strong>educational reference</strong>.
        It is not, and is not intended to be, a Clinical Decision Support System
        (CDS) as defined under the FDA Software as a Medical Device (SaMD)
        framework. The toolkit provides calculator outputs, validated
        assessment scoring, and lookups of published criteria. It does
        <em> not </em>provide patient-specific dose recommendations or
        diagnostic outputs.
      </p>
      <p>
        Pharmacist clinical judgment is required for every patient-specific
        decision. The prescriber remains responsible for medication orders.
      </p>

      <h2>Calculators — citations and limitations</h2>

      <h3>1. Cockcroft-Gault creatinine clearance</h3>
      <p>
        Cockcroft DW, Gault MH. Prediction of creatinine clearance from serum
        creatinine. <em>Nephron</em> 1976;16(1):31-41. Validity: stable serum
        creatinine; uses actual body weight. Not appropriate for acute kidney
        injury or patients at extremes of muscle mass without adjusted body
        weight correction.
      </p>

      <h3>2. MDRD eGFR (4-variable)</h3>
      <p>
        Levey AS et al. Using standardized serum creatinine values in the MDRD
        study equation. <em>Ann Intern Med</em> 2006;145(4):247-254. Race
        coefficient (1.212 for Black race) is reproduced for documentation
        only; the 2021 NKF/ASN Task Force recommends removing race. Prefer
        CKD-EPI 2021.
      </p>

      <h3>3. CKD-EPI 2021 (race-free)</h3>
      <p>
        Inker LA, Eneanya ND et al. New creatinine- and cystatin C-based
        equations to estimate GFR without race. <em>NEJM</em> 2021;385(19):1737-1749.
        Preferred eGFR formula per the 2021 NKF/ASN Task Force.
      </p>

      <h3>4. Adjusted Body Weight</h3>
      <p>
        Pai MP, Paloucek FP. The origin of the "ideal" body weight equations.
        <em> Ann Pharmacother</em> 2000;34(9):1066-1069. Commonly applied when
        actual body weight exceeds 130% of ideal body weight. Correction
        factor 0.4 is most-cited; 0.3 and 0.5 are also used in specific
        protocols (aminoglycosides).
      </p>

      <h3>5. Ideal Body Weight (Devine)</h3>
      <p>
        Devine BJ. Gentamicin therapy. <em>Drug Intell Clin Pharm</em>
        1974;8:650-655. Assumes height ≥ 60 in. Hamwi, Robinson, or Miller
        formulas may be preferred at extremes.
      </p>

      <h3>6. BMI</h3>
      <p>
        World Health Organization. Obesity: preventing and managing the global
        epidemic. WHO Technical Report Series 894. 2000.
      </p>

      <h3>7. Anion Gap</h3>
      <p>
        Kraut JA, Madias NE. Serum anion gap: its uses and limitations in
        clinical medicine. <em>Nat Rev Nephrol</em> 2007;3:274-285. Consider
        albumin correction (decrease 2.5 mEq/L per 1 g/dL fall in albumin).
      </p>

      <h3>8. Corrected calcium for hypoalbuminemia</h3>
      <p>
        Payne RB et al. Interpretation of serum calcium in patients with
        abnormal serum proteins. <em>Br Med J</em> 1973;4:643-646. Adjustment
        becomes less reliable in critical illness; ionized calcium is
        preferred when available.
      </p>

      <h3>9. Phenytoin corrected level (Sheiner-Tozer)</h3>
      <p>
        Sheiner LB, Tozer TN. <em>Clin Pharmacol Ther</em> 1973;14(4):553-558.
        Toolkit applies the renal-impaired denominator (0.275·Alb + 0.1) when
        CrCl &lt; 25 mL/min and the standard denominator (0.2·Alb + 0.1)
        otherwise. Confirm with free phenytoin when feasible.
      </p>

      <h3>10. Vancomycin AUC24 — estimator only</h3>
      <p>
        Matzke GR et al. Pharmacokinetics of vancomycin in patients with
        various degrees of renal function. <em>Antimicrob Agents Chemother</em>
        1984;25(4):433-437. Target AUC24/MIC interpretation per Rybak MJ et al.
        Therapeutic monitoring of vancomycin for serious MRSA infections.
        <em> AJHP</em> 2020;77(11):835-864.
      </p>
      <p>
        <strong>The vancomycin AUC tool is an estimator only.</strong> It is
        not, and must not be used as, a dosing recommendation. Use a Bayesian
        software platform or measured trough/peak with PK consultation for
        prescriptive dosing decisions.
      </p>

      <h3>11. Warfarin dose titration (aggregate published guidance)</h3>
      <p>
        Garcia DA, Witt DM, Hylek E et al. Delivery of optimized
        anticoagulant therapy: consensus statement from the Anticoagulation
        Forum. <em>Chest</em> 2008;133(6 Suppl):234S-256S (American College of
        Chest Physicians). The toolkit surfaces aggregate published
        guidance bands and is not a prescriptive dose recommendation.
        Pharmacist must verify protocol with prescriber.
      </p>

      <h3>12. CrCl-adjusted renal dose lookup</h3>
      <p>
        Doses are paraphrased from current FDA labels via DailyMed. Each
        record includes a label citation. Verify against the most-current
        prescribing information at <a href="https://dailymed.nlm.nih.gov">dailymed.nlm.nih.gov</a>.
        The renal lookup is a documentation aid; pharmacist judgment is
        required.
      </p>

      <h3>13. Absolute Neutrophil Count (ANC)</h3>
      <p>
        Severity bands per the NCI Common Terminology Criteria for Adverse
        Events (CTCAE) v5.0. Bethesda, MD: National Institutes of Health,
        National Cancer Institute, 2017.
      </p>

      <h2>Assessment scales — citations</h2>

      <h3>GDS-15 (Geriatric Depression Scale Short Form)</h3>
      <p>
        Sheikh JI, Yesavage JA. Geriatric Depression Scale (GDS) recent
        evidence and development of a shorter version. <em>Clinical
        Gerontologist</em> 1986;5:165-173. Public domain.
      </p>

      <h3>MMSE (Mini-Mental State Examination)</h3>
      <p>
        Folstein MF, Folstein SE, McHugh PR. "Mini-mental state": a practical
        method for grading the cognitive state of patients for the clinician.
        <em> J Psychiatr Res</em> 1975;12(3):189-198. The MMSE items are
        licensed by Psychological Assessment Resources (PAR). WeConsult Rx
        does not include the MMSE item content; the toolkit provides a
        score-input form for documentation only. Administer the MMSE via a
        licensed copy.
      </p>

      <h3>Mini-Cog</h3>
      <p>
        Borson S, Scanlan J, Brush M, Vitaliano P, Dokmak A. The Mini-Cog: a
        cognitive "vital signs" measure for dementia screening in
        multi-lingual elderly. <em>Int J Geriatr Psychiatry</em>
        2000;15(11):1021-1027. Public domain.
      </p>

      <h3>Morse Fall Scale</h3>
      <p>
        Morse JM, Morse RM, Tylko SJ. Development of a scale to identify the
        fall-prone patient. <em>Can J Aging</em> 1989;8(4):366-377. Public
        domain.
      </p>

      <h3>MNA-SF (Mini Nutritional Assessment Short Form)</h3>
      <p>
        Rubenstein LZ, Harker JO, Salva A, Guigoz Y, Vellas B. Screening for
        undernutrition in geriatric practice: developing the short-form
        mini-nutritional assessment (MNA-SF). <em>J Gerontol A Biol Sci Med
        Sci</em> 2001;56(6):M366-372.
      </p>

      <h3>CAM (Confusion Assessment Method)</h3>
      <p>
        Inouye SK, van Dyck CH, Alessi CA, Balkin S, Siegal AP, Horwitz RI.
        Clarifying confusion: the confusion assessment method. A new method
        for detection of delirium. <em>Ann Intern Med</em> 1990;113(12):941-948.
      </p>

      <h2>Lookups — citations</h2>

      <h3>AGS Beers Criteria 2023</h3>
      <p>
        American Geriatrics Society 2023 Updated AGS Beers Criteria for
        Potentially Inappropriate Medication Use in Older Adults. <em>J Am
        Geriatr Soc</em> 2023;71(7):2052-2081.
      </p>

      <h3>STOPP/START version 3 (2023)</h3>
      <p>
        O'Mahony D, Cherubini A, Guiteras AR, et al. STOPP/START criteria for
        potentially inappropriate prescribing in older people: version 3.
        <em> Eur Geriatr Med</em> 2023;14:625-632.
      </p>

      <h3>Drug-disease and drug-food interactions</h3>
      <p>
        Sourced from current FDA labels (Section 7 — Drug Interactions) via
        DailyMed at <a href="https://dailymed.nlm.nih.gov">dailymed.nlm.nih.gov</a>.
        Records include the source SPL identifier where available. Verify
        against the most-current label.
      </p>

      <h2>Updates and corrections</h2>
      <p>
        Citations and source corpora are reviewed at least annually. Updates
        are tracked in the WeConsult Rx changelog. To report a calculator or
        citation issue, contact <a href="mailto:support@weconsultrx.com">support@weconsultrx.com</a>.
      </p>
    </LegalPage>
  );
}
